import { useEffect, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography, Space, Empty, Button, Input, Select, DatePicker, Tooltip, Checkbox } from 'antd';
import { DownloadOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { api, User, isAdminRole } from '../api';
import { t } from '../i18n';
import { downloadXlsx, stampNow, XlsxRecord, XlsxSheet } from '../xlsx';
import { fmtDateTime } from '../time';
import { diffChars, Seg } from '../diff';

type FlashHistoryItem = Awaited<ReturnType<typeof api.flashHistory>>['items'][number];

/** Prettify a raw change path for display (drop the `elabelFields.` prefix). */
function fieldLabel(path: string): string {
  return path.replace(/^elabelFields\./, '');
}

// Monospace for the change values so before/after line up character-by-character.
const MONO =
  "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, 'Courier New', monospace";

// Extra emphasis on the characters that actually changed — a red marker that
// reads on top of both the neutral "before" and green "after" text. Only colour
// and background differ; the weight matches the surrounding text so the
// monospace glyphs never look like a different font.
const HL: CSSProperties = {
  color: '#f5222d',
  background: 'rgba(245,34,45,0.15)',
  borderRadius: 2,
  padding: '0 1px',
  fontFamily: MONO,
};

/** Render one side of a change: common characters keep the side's base colour
 * ("default" = the theme text colour for "before", success green for "after");
 * changed characters get the red highlight on top. Same char-level diff the
 * XLSX export uses. */
function Segs({ segs, base }: { segs: Seg[]; base: 'default' | 'success' }) {
  return (
    <>
      {segs.map((s, i) =>
        s.diff ? (
          <span key={i} style={HL}>
            {s.text}
          </span>
        ) : (
          <Typography.Text
            key={i}
            type={base === 'success' ? 'success' : undefined}
            style={{ fontFamily: MONO }}
          >
            {s.text}
          </Typography.Text>
        ),
      )}
    </>
  );
}

function statusTag(st: string) {
  if (st === 'OK') return <Tag color="green">OK</Tag>;
  if (st === 'not_confirmed') return <Tag color="orange">{t('flashPending')}</Tag>;
  if (st === 'unknown') return <Tag>?</Tag>;
  return <Tag color="red">{st}</Tag>;
}

const DISK_TYPES: Record<string, { label: string; color: string }> = {
  nvme: { label: 'NVMe', color: 'geekblue' },
  sas25: { label: 'SAS 2.5″', color: 'green' },
  sas35: { label: 'SAS 3.5″', color: 'gold' },
};

/** Transport + form factor snapshot recorded at flash time. */
function diskTypeTag(dt: string | null) {
  const m = dt ? DISK_TYPES[dt] : undefined;
  if (m) return <Tag color={m.color}>{m.label}</Tag>;
  return <Typography.Text type="secondary">—</Typography.Text>;
}

const fmt = fmtDateTime;

/** Standalone flash-history page (in the main menu, visible to every user). A
 * client sees only flashes on their own devices (+ ones they applied); admins
 * see all. Rows are per flashed disk in a flat time-sorted list by default; a
 * "Group by device" checkbox switches to the per-device grouped view. */
export function FlashHistory({ user }: { user: User }) {
  const isAdmin = isAdminRole(user.role);
  const q = useQuery({
    queryKey: ['flash-history-page'],
    queryFn: () => api.flashHistory({ limit: 500 }),
    refetchInterval: 20_000,
  });
  // Clients can only list their own devices; admins list all — either way this
  // just supplies human labels for the device column.
  const devices = useQuery({
    queryKey: isAdmin ? ['devices'] : ['my-devices'],
    queryFn: isAdmin ? api.listDevices : api.listMyDevices,
  });
  const deviceLabel = (id: string | null): string => {
    if (!id || id === '—') return t('flashUnknownDevice');
    const d = devices.data?.find((x) => x.id === id);
    return d?.label || id.substring(0, 8);
  };

  // Filters (client-side over the fetched window).
  // Pre-fill filters from the URL (?device=… / ?who=…) so the dashboard's Top
  // devices / Top clients tables can deep-link into a filtered history.
  const [searchParams] = useSearchParams();
  const [deviceF, setDeviceF] = useState<string | undefined>(searchParams.get('device') ?? undefined);
  const [snF, setSnF] = useState('');
  const [whoF, setWhoF] = useState(searchParams.get('who') ?? '');
  const [statusF, setStatusF] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  // Group rows by device — opt-in (off by default: a flat, time-sorted list).
  const [groupByDevice, setGroupByDevice] = useState(false);

  // Keep the device/who filters in sync when the URL params actually change
  // (e.g. navigating here fresh with no params, or via a new dashboard deep-link
  // while this component is already mounted). Depending on the param values
  // rather than the searchParams object means a manual filter edit — which does
  // not touch the URL — is left untouched.
  const deviceParam = searchParams.get('device');
  const whoParam = searchParams.get('who');
  useEffect(() => {
    setDeviceF(deviceParam ?? undefined);
    setWhoF(whoParam ?? '');
  }, [deviceParam, whoParam]);

  const allRows = (q.data?.items ?? []).flatMap((it, i) =>
    it.disks.map((d, j) => ({
      key: `${it.sessionId ?? it.whenUnix}-${i}-${j}`,
      whenUnix: it.whenUnix,
      actorUsername: it.actorUsername,
      actorId: it.actorId,
      actorKind: it.actorKind,
      deviceId: it.deviceId,
      serial: d.serial,
      status: d.status,
      diskType: d.diskType,
      changes: d.changes,
    })),
  );
  type DiskRow = (typeof allRows)[number];

  // Device options: only devices that actually appear in the history.
  const deviceOptions = [...new Set(allRows.map((r) => r.deviceId ?? '—'))].map((id) => ({
    value: id,
    label: deviceLabel(id === '—' ? null : id),
  }));

  const fromTs = range?.[0] ? range[0].startOf('day').unix() : undefined;
  const toTs = range?.[1] ? range[1].endOf('day').unix() : undefined;
  const matchesStatus = (st: string): boolean => {
    if (!statusF) return true;
    if (statusF === 'OK') return st === 'OK';
    if (statusF === 'pending') return st === 'not_confirmed';
    // 'failed' = an actual device failure, NOT the neutral 'unknown'/'?' bucket.
    return st !== 'OK' && st !== 'not_confirmed' && st !== 'unknown';
  };
  const diskRows = allRows.filter(
    (r) =>
      (!deviceF || (r.deviceId ?? '—') === deviceF) &&
      (!snF || r.serial.toLowerCase().includes(snF.toLowerCase())) &&
      (!whoF ||
        (r.actorUsername ?? '').toLowerCase().includes(whoF.toLowerCase()) ||
        (r.actorId ?? '').toLowerCase().includes(whoF.toLowerCase())) &&
      matchesStatus(r.status) &&
      (fromTs === undefined || r.whenUnix >= fromTs) &&
      (toTs === undefined || r.whenUnix <= toTs),
  );

  const deviceIds = [...new Set(diskRows.map((r) => r.deviceId ?? '—'))];
  // Group only when the operator asked for it AND there is more than one device.
  const grouped = groupByDevice && deviceIds.length > 1;

  /** Spreadsheet dump of exactly what the table currently shows (all active
   * filters applied). Two rows per flashed disk — the values before the flash
   * on top, after it below — with when/device/who/disk merged across the pair.
   * A field the flash didn't touch is blank in BOTH rows: the history stores
   * only the changes, so we have no unchanged values to print. */
  const exportXls = (): void => {
    // Field name → column, `elabelFields.` prefix stripped (bom/level are bare).
    const FIELDS = ['bom', 'level', 'barcode', 'manufactured', 'boardtype', 'model', 'description'];
    const sheet: XlsxSheet = {
      name: t('xlsSheetHistory'),
      single: [
        { header: t('historyWhen'), width: 19 },
        { header: t('historyDevice'), width: 20 },
        { header: t('historyDiskSn'), width: 22 },
        { header: t('historyDiskType'), width: 11 },
      ],
      pair: [
        { header: 'BOM', width: 14 },
        { header: 'Level', width: 9 },
        { header: 'BarCode', width: 24 },
        { header: 'Manufactured', width: 14 },
        { header: 'BoardType', width: 18 },
        { header: 'Model', width: 22 },
        { header: 'Description', width: 46 },
      ],
      wasLabel: t('xlsWasRow'),
      nowLabel: t('xlsNowRow'),
    };
    const records: XlsxRecord[] = [...diskRows]
      .sort((a, b) => b.whenUnix - a.whenUnix)
      .map((r) => {
        const byField = new Map(r.changes.map((c) => [fieldLabel(c.field).toLowerCase(), c]));
        return {
          single: [
            fmt(r.whenUnix),
            deviceLabel(r.deviceId),
            r.serial,
            (r.diskType && DISK_TYPES[r.diskType]?.label) || '',
          ],
          was: FIELDS.map((f) => byField.get(f)?.from ?? ''),
          now: FIELDS.map((f) => byField.get(f)?.to ?? ''),
        };
      });
    downloadXlsx(`flash-history_${stampNow()}`, sheet, records);
  };

  const changesCol: ColumnsType<DiskRow>[number] = {
    title: t('flashChanges'),
    key: 'ch',
    render: (_: unknown, r) => {
      if (!r.changes.length) return <Typography.Text type="secondary">—</Typography.Text>;
      // Each change is its own bordered chip; the chips flow left-to-right and
      // wrap (flex-wrap). Inside a chip the values stay stacked vertically
      // (field / before / after) as before. A bounded container width makes the
      // chips wrap instead of stretching the column.
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: 680 }}>
          {r.changes.map((c) => {
            // A real edit (both sides present) → char-diff both, red highlight on
            // the changed characters. A field that was empty before has nothing
            // to diff against, so just show the new value.
            const diffable = c.from != null && c.from !== '' && c.from !== c.to;
            const d = diffable ? diffChars(c.from as string, c.to) : null;
            return (
              <div
                key={c.field}
                style={{
                  border: '1px solid rgba(140,140,140,0.28)',
                  borderRadius: 6,
                  padding: '3px 8px',
                  fontSize: 12,
                  lineHeight: 1.35,
                  maxWidth: 320,
                  wordBreak: 'break-word',
                }}
              >
                <div>
                  <Typography.Text type="secondary">{fieldLabel(c.field)}</Typography.Text>
                </div>
                {d ? (
                  <div style={{ fontFamily: MONO }}>
                    <div>
                      <Segs segs={d.was} base="default" />
                    </div>
                    <div>
                      <Segs segs={d.now} base="success" />
                    </div>
                  </div>
                ) : (
                  <div style={{ fontFamily: MONO }}>
                    <Typography.Text type="success" style={{ fontFamily: MONO }}>
                      {c.to}
                    </Typography.Text>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    },
  };
  const whoCol: ColumnsType<DiskRow>[number] = {
    title: t('dashColClient'),
    dataIndex: 'actorUsername',
    render: (v: string | null, r) => (
      <Space size={6}>
        <span>{v ?? r.actorId?.slice(0, 8) ?? '—'}</span>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          ({r.actorKind})
        </Typography.Text>
      </Space>
    ),
  };
  const diskColumns: ColumnsType<DiskRow> = [
    {
      title: t('historyWhen'),
      dataIndex: 'whenUnix',
      width: 160,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.whenUnix - b.whenUnix,
      render: (v: number) => <Typography.Text type="secondary">{fmt(v)}</Typography.Text>,
    },
    whoCol,
    {
      title: t('historyDiskSn'),
      dataIndex: 'serial',
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    { title: t('historyDiskType'), dataIndex: 'diskType', width: 100, render: (v: string | null) => diskTypeTag(v) },
    changesCol,
    { title: t('historyResult'), dataIndex: 'status', width: 110, render: (v: string) => statusTag(v) },
  ];
  // In the flat (ungrouped) view with more than one device, add a Device column
  // after the time so each row is attributable; a single device makes it noise.
  const flatColumns: ColumnsType<DiskRow> =
    !grouped && deviceIds.length > 1
      ? [
          diskColumns[0]!,
          {
            title: t('historyDevice'),
            dataIndex: 'deviceId',
            width: 160,
            render: (id: string | null) => <span>{deviceLabel(id)}</span>,
          },
          ...diskColumns.slice(1),
        ]
      : diskColumns;

  interface DevGroup {
    deviceId: string;
    count: number;
    lastWhen: number;
    rows: DiskRow[];
  }
  const groups: DevGroup[] = grouped
    ? deviceIds
        .map((id) => {
          const rows = diskRows.filter((r) => (r.deviceId ?? '—') === id);
          return { deviceId: id, count: rows.length, lastWhen: Math.max(...rows.map((r) => r.whenUnix)), rows };
        })
        .sort((a, b) => b.lastWhen - a.lastWhen)
    : [];

  const groupColumns: ColumnsType<DevGroup> = [
    { title: t('historyDevice'), dataIndex: 'deviceId', render: (id: string) => <strong>{deviceLabel(id)}</strong> },
    {
      title: t('flashHistoryLastFlash'),
      dataIndex: 'lastWhen',
      width: 180,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.lastWhen - b.lastWhen,
      render: (v: number) => <Typography.Text type="secondary">{fmt(v)}</Typography.Text>,
    },
    { title: t('flashChangesCount'), dataIndex: 'count', width: 90, align: 'right' as const },
  ];

  return (
    <Card
      title={<Space><ThunderboltOutlined />{t('flashHistoryTitle')}</Space>}
      extra={
        <Space>
          <Tooltip title={t('exportXlsTip')}>
            <Button icon={<DownloadOutlined />} onClick={exportXls} disabled={diskRows.length === 0}>
              {t('exportXls')}
            </Button>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={() => q.refetch()} loading={q.isFetching}>
            {t('dashRefresh')}
          </Button>
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          style={{ minWidth: 200 }}
          placeholder={t('historyAnyDevice')}
          allowClear
          value={deviceF}
          onChange={setDeviceF}
          options={deviceOptions}
        />
        <Input
          placeholder={`${t('historyDiskSn')}…`}
          value={snF}
          onChange={(e) => setSnF(e.target.value)}
          style={{ width: 200 }}
          allowClear
        />
        {isAdmin && (
          <Input
            placeholder={`${t('dashColClient')}…`}
            value={whoF}
            onChange={(e) => setWhoF(e.target.value)}
            style={{ width: 160 }}
            allowClear
          />
        )}
        <Select
          style={{ minWidth: 150 }}
          placeholder={t('historyResult')}
          allowClear
          value={statusF}
          onChange={setStatusF}
          options={[
            { value: 'OK', label: 'OK' },
            { value: 'failed', label: t('editorApplyFailed') },
            { value: 'pending', label: t('flashPending') },
          ]}
        />
        <DatePicker.RangePicker
          value={range as [Dayjs, Dayjs] | null}
          onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
          allowEmpty={[true, true]}
        />
        <Checkbox checked={groupByDevice} onChange={(e) => setGroupByDevice(e.target.checked)}>
          {t('flashGroupByDevice')}
        </Checkbox>
      </Space>
      {grouped ? (
        <Table<DevGroup>
          size="small"
          rowKey="deviceId"
          loading={q.isLoading}
          dataSource={groups}
          columns={groupColumns}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description={t('emptyList')} /> }}
          expandable={{
            defaultExpandAllRows: true,
            expandedRowRender: (g) => (
              <Table<DiskRow> size="small" rowKey="key" dataSource={g.rows} columns={diskColumns} pagination={false} scroll={{ x: 'max-content' }} />
            ),
          }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
        />
      ) : (
        <Table<DiskRow>
          size="small"
          rowKey="key"
          loading={q.isLoading}
          dataSource={diskRows}
          columns={flatColumns}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description={t('emptyList')} /> }}
          pagination={{ pageSize: 30, showSizeChanger: false }}
        />
      )}
    </Card>
  );
}
